import type { QuantityUnit } from '@sbg/shared/constants';

export interface ContactCopy {
  seoTitle: string;
  seoDescription: string;
  eyebrow: string;
  title: string;
  lead: string;
  details: {
    title: string;
    address: string;
    hours: string;
    phone: string;
    email: string;
    whatsapp: string;
    chat: string;
    follow: string;
    /** The line above the reasons to write to us, and the reasons. */
    reasonsTitle: string;
    reasons: readonly string[];
    /** What the WhatsApp button on this page starts the chat with. */
    whatsappMessage: string;
  };
  form: {
    title: string;
    lead: string;
    signedInAs: string;
    name: string;
    namePlaceholder: string;
    phone: string;
    /** The number box's placeholder: an example national number (the country code is the button beside it). */
    phonePlaceholder: string;
    /** Name of the country-code button ("Country code"), of the list's search box, and its empty state. */
    countryCode: string;
    countrySearch: string;
    countryNone: string;
    email: string;
    city: string;
    /** The heading over the three optional fields: what is needed. */
    needs: string;
    thickness: string;
    thicknessPlaceholder: string;
    quantity: string;
    quantityPlaceholder: string;
    /** Accessible name of the box that picks the quantity's unit. */
    quantityUnit: string;
    units: Record<QuantityUnit, string>;
    size: string;
    sizePlaceholder: string;
    /** Heading of the chips of the stone the visitor arrived with, and the chip's remove button. */
    aboutStone: string;
    removeStone: string;
    message: string;
    messagePlaceholder: string;
    attachment: string;
    attachmentHelp: string;
    attachmentRemove: string;
    attachmentTooBig: string;
    attachmentType: string;
    consent: string;
    privacy: string;
    submit: string;
    sending: string;
    optional: string;
    captchaFailed: string;
  };
  topics: Record<'samples' | 'consultation' | 'quote', string>;
  success: {
    title: string;
    reference: string;
    body: string;
    again: string;
    browse: string;
  };
  errors: { network: string; rateLimited: string; generic: string };
}

export const content = {
  seoTitle: 'Contact Us: Ask for a Granite Price',
  seoDescription:
    'Get a granite price from Shree Balaji Granites, Rajsamand, Rajasthan. Send an enquiry or WhatsApp us for granite slabs, tiles, countertops, samples and advice.',
  eyebrow: 'Tell us your plan. We bring the stone.',
  title: 'Contact us for a granite price',
  lead: 'Planning a kitchen, a floor, stairs or a wall? Tell us what you have in mind. We will suggest the right granite and send you samples and a price. Write, call or WhatsApp us from anywhere.',
  details: {
    title: 'Visit or call us',
    address: 'Address',
    hours: 'Open hours',
    phone: 'Phone',
    email: 'Email',
    whatsapp: 'WhatsApp',
    chat: 'Message us on WhatsApp',
    follow: 'Follow us',
    reasonsTitle: 'Why ask us?',
    reasons: [
      'Share your plan and we will suggest the right granite.',
      'Get a price for your size and quantity.',
      'Ask for samples before you decide.',
      'Send a photo or drawing of your space.',
    ],
    whatsappMessage:
      'Hello Shree Balaji Granites,\n\nI am interested in granite for my project and would be grateful if you could share the price and the options available.\n\nI can send my size and photos for your reference. Kindly call or message me at your convenience.\n\nThank you.',
  },
  form: {
    title: 'Send us a message',
    lead: 'Share your plan, size and city. The more you tell us, the better we can help. We read every message.',
    signedInAs: 'You are signed in as {name}. We filled in your details.',
    name: 'Your name',
    namePlaceholder: 'Full name',
    phone: 'Phone number',
    phonePlaceholder: '9024780751',
    countryCode: 'Country code',
    countrySearch: 'Type a country name',
    countryNone: 'No country found',
    email: 'Email',
    city: 'City',
    needs: 'What do you need?',
    thickness: 'Thickness (mm)',
    thicknessPlaceholder: 'Example: 18',
    quantity: 'Quantity',
    quantityPlaceholder: 'Example: 250',
    quantityUnit: 'Pick a unit',
    units: { SQ_FT: 'sq ft', SLAB: 'slabs' },
    size: 'Size',
    sizePlaceholder: 'Example: 8 × 4 ft',
    aboutStone: 'This message is about',
    removeStone: 'Remove {name}',
    message: 'Your message',
    messagePlaceholder:
      'Example: Red granite for a kitchen countertop and a staircase, about 150 sq ft, needed in two months.',
    attachment: 'Upload a photo or drawing',
    attachmentHelp: 'JPG, PNG, WebP or PDF. Up to 8 MB.',
    attachmentRemove: 'Remove the file',
    attachmentTooBig: 'This file is bigger than 8 MB.',
    attachmentType: 'Please pick a JPG, PNG, WebP or PDF file.',
    consent:
      'I agree that Shree Balaji Granites may contact me about this message. I have read the {privacy}.',
    privacy: 'Legal Disclaimer',
    submit: 'Send message',
    sending: 'Sending…',
    optional: 'optional',
    captchaFailed: 'The safety check did not finish. Please send again.',
  },
  topics: {
    samples: 'I would like samples of the stones I listed. Please tell me the price and sizes too.',
    consultation: 'I need advice on the right granite for my project.',
    quote: 'I would like a price for a project like the ones in your gallery.',
  },
  success: {
    title: 'Thank you. We got your message.',
    reference: 'Your reference number',
    body: 'We will contact you using the details you gave us. Keep your plan and size ready. It helps us give you a clear price.',
    again: 'Send another message',
    browse: 'See more granite',
  },
  errors: {
    network: 'We could not reach the server. Please check your internet and try again.',
    rateLimited: 'You sent many messages in a short time. Please wait a little and try again.',
    generic: 'Something went wrong. Please try again.',
  },
} satisfies ContactCopy;
